import { to } from 'rails-timezone'
import Model from './model.js'
import { i18n } from '../i18n'
const { t } = i18n.global
import dayjs from '../dayjs.js'
import { computeLocale } from '../../utils/computeLocale.js'

let store = null
let vm = null

const Helpers = {
  getSubscriber: (vm, token) => {
    return vm.config.globalProperties.$helpers.decodeToken(token).sub
  }
}

export default {
  install: (Vue, options) => {
    store = options.store
    vm = Vue
    let Proxy = Model.init(options)
    const $axios = Proxy.axios.create({ baseURL: options.apiUrl })
    Proxy.axios = $axios
    Proxy.insertMiddlewareBefore('axios-request', {
      name: 'before-axios-request',
      req: function (payload) {
        payload.req.timeout = 10000
        return payload
      }
    })

    Proxy.isOnMobile = false

    Proxy.axios.interceptors.request.use(async function (config) {
      config.headers['X-Is-Request-From-Mobile'] = Proxy.isOnMobile
      const timezone = dayjs.tz.guess()
      config.headers['Accept-Language'] = (await computeLocale()).locale
      config.headers['X-Timezone'] = to(timezone)
      return config
    })

    // ******************************       HELPERS        ******************************
    Proxy.authenticate = (credentials, device, appVersion) => {
      return new Promise((resolve, reject) => {
        Proxy.axios
          .post(
            Proxy.apiUrl + '/auth',
            {
              auth: {
                email: credentials.email,
                password: credentials.password
              },
              device: {
                manufacturer: device.manufacturer,
                model: device.model,
                platform: device.platform,
                version: device.version
              },
              app: {
                versionNumber: appVersion || process.env.APP_VERSION
              }
            },
            {
              'Content-Type': 'application/vnd.api+json',
              Accept: 'application/vnd.api+json',
              headers: {
                'x-requested-roles': 'reporter,checklister,manager'
              }
            }
          )
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => {
                  reject(response)
                })
            }
          )
      })
    }

    Proxy.msalAuthenticate = (credentials, device, appVersion) => {
      return new Promise((resolve, reject) => {
        Proxy.axios
          .post(
            Proxy.apiUrl + '/auth',
            {
              auth: {
                email: credentials.email,
                msalToken: credentials.msalToken
              },
              device: {
                manufacturer: device.manufacturer,
                model: device.model,
                platform: device.platform,
                version: device.version
              },
              app: {
                versionNumber: appVersion.versionNumber || process.env.APP_VERSION
              }
            },
            {
              'Content-Type': 'application/vnd.api+json',
              Accept: 'application/vnd.api+json',
              headers: {
                'x-requested-roles': 'reporter,checklister,manager'
              }
            }
          )
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => {
                  reject(response)
                })
            }
          )
      })
    }

    Proxy.authenticateWithToken = (credentials) => {
      return new Promise((resolve, reject) => {
        Proxy.axios
          .post(
            Proxy.apiUrl + `/auth`,
            {
              auth: { email: credentials.email, token: credentials.token }
            },
            {
              'Content-Type': 'application/vnd.api+json',
              Accept: 'application/vnd.api+json'
            }
          )
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              console.error('passwordReset => Error')
              reject(error)
            }
          )
      })
    }

    Proxy.resetPassword = (email) => {
      return new Promise((resolve, reject) => {
        Proxy.axios
          .post(
            Proxy.apiUrl + '/auth/reset_password',
            {
              auth: { email: email, base: 'https://reporter.sowellapp.com' }
            },
            {
              'Content-Type': 'application/vnd.api+json',
              Accept: 'application/vnd.api+json'
            }
          )
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              console.error('resetPassword => Error')
              reject(error)
            }
          )
      })
    }

    // NOTE: magiclink endpoints are deliberately unauthenticated - the user has no
    // token yet (first login) or cannot log in anymore (forgotten password). Do NOT
    // add `Proxy.headers['Authorization']` here.
    // Routes are the V1 ones (`/auth/magiclink`), NOT the V2 `/request-magiclink`:
    // using the V2 names against api.sowellapp.com returns a 404, which the UI would
    // report as "no matching account".
    Proxy.requestMagicLink = (email, channel, baseUrl) => {
      const auth = { email: email }
      if (channel) auth.channel = channel
      if (baseUrl) auth.base_url = baseUrl

      return new Promise((resolve, reject) => {
        Proxy.axios
          .post(
            Proxy.apiUrl + '/auth/magiclink',
            { auth: auth },
            {
              'Content-Type': 'application/vnd.api+json',
              Accept: 'application/vnd.api+json'
            }
          )
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              reject(error)
            }
          )
      })
    }

    Proxy.validateMagicLink = (magic, password) => {
      return new Promise((resolve, reject) => {
        Proxy.axios
          .post(
            Proxy.apiUrl + '/auth/magiclink/validate',
            { magic: magic, password: password },
            {
              'Content-Type': 'application/vnd.api+json',
              Accept: 'application/vnd.api+json'
            }
          )
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              reject(error)
            }
          )
      })
    }

    Proxy.loadReporter = () => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.find(
          'reporter',
          Helpers.getSubscriber(vm, store.getters['reporter/token']),
          {
            'fields[reporters]':
              'email,first-name,last-name,img,company,recipients,can-close-issues',
            // NOTE: uncomment this when the API is updated
            // 'fields[companies]':
            // 'id,name,updated-at,show-is-private,external-issues-url,forward-issues,has-audits,allow-unplanned-audits,show-unscoped-history,is-checkpoints-default-ok,last-reset-date,suggest-providers-in-security',

            include: 'company'
          }
        ).then(
          (response) => {
            resolve(response)
          },
          (error) => {
            vm.config.globalProperties.$ErrorHandler
              .process('', error)
              .then((response) => {
                reject(response)
              })
          }
        )
      })
    }

    Proxy.loadReporterScopes = () => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one(
          'reporter',
          Helpers.getSubscriber(vm, store.getters['reporter/token'])
        )
          .all('scopes')
          .get({ 'fields[scopes]': 'name,updated-at' })
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => {
                  reject(response)
                })
            }
          )
      })
    }

    Proxy.loadCompanyCategories = (company) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one('company', company.id)
          .all('categories')
          .get({
            'fields[categories]':
              'id,name,img,has-reasons,reasons,is-cleaning-related,allow-tenant-request,is-hidden,childs,base-category',
            'fields[reasons]': 'id,name',
            include: 'childs.reasons,base-category'
          })
          .then((response) => {
            resolve(response)
          })
          .catch((error) => {
            vm.config.globalProperties.$ErrorHandler
              .process('', error)
              .then((response) => {
                reject(response)
              })
          })
      })
    }

    Proxy.loadCategoryReasons = (categoryId) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one('category', categoryId)
          .all('reasons')
          .get({
            'fields[reasons]': 'id,name'
          })
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => {
                  reject(response)
                })
            }
          )
      })
    }

    Proxy.loadReporterResidences = () => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one(
          'reporter',
          Helpers.getSubscriber(vm, store.getters['reporter/token'])
        )
          .all('residences')
          .get({
            'fields[zones]': 'id,name',
            'fields[residences]': 'id,name,agency,places',
            'fields[places]': 'id,name',
            include: 'agency,places'
          })
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => {
                  reject(response)
                })
            }
          )
      })
    }

    Proxy.loadReporterPlaces = () => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one(
          'reporter',
          Helpers.getSubscriber(vm, store.getters['reporter/token'])
        )
          .all('places')
          .get({
            'fields[places]': 'id,name,residence',
            'fields[residences]': 'id,name,agency,places',
            'fields[zones]': 'id,name',
            'page[limit]': 200,
            include: 'residence.agency'
          })
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => {
                  reject(response)
                })
            }
          )
      })
    }

    Proxy.loadReporterPlacesIds = async () => {
      const PAGINATION = 1000
      let result = []

      const getPlaces = (offset, limit) => {
        return new Promise((resolve, reject) => {
          Proxy.headers['Authorization'] =
            'Bearer ' + store.getters['reporter/token']
          Proxy.one(
            'reporter',
            Helpers.getSubscriber(vm, store.getters['reporter/token'])
          )
            .all('places')
            .get({
              'fields[places]': 'id',
              'page[limit]': limit,
              'page[offset]': offset
            })
            .then(
              (response) => {
                resolve(response)
              },
              (error) => {
                vm.prototype.$ErrorHandler
                  .process('', error)
                  .then((response) => {
                    reject(response)
                  })
              }
            )
        })
      }

      let recordCount
      do {
        try {
          const response = await getPlaces(result.length, PAGINATION)
          result = [...result, ...response.data]
          recordCount = response.meta['record-count']
        } catch (error) {
          if (error) return Promise.reject(error)
        }
      } while (result.length < recordCount)

      return Promise.resolve(result)
    }

    Proxy.loadAgencyResidenceOfPlace = (placeId) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.find('place', placeId, {
          'fields[places]': 'id,name,residence',
          'fields[residences]': 'id,name,agency',
          'fields[agencies]': 'id,name',
          include: 'residence.agency'
        }).then(
          (response) => {
            resolve(response)
          },
          (error) =>
            vm.prototype.$ErrorHandler
              .process('', error)
              .then((response) => reject(response))
        )
      })
    }

    Proxy.loadAgencyResidenceOfPlaces = (placeIds) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one(
          'reporter',
          Helpers.getSubscriber(vm, store.getters['reporter/token'])
        )
          .all('places')
          .get({
            'fields[places]': 'id,name,residence,spots',
            'fields[residences]': 'id,name,agency',
            'fields[agencies]': 'id,name',
            'fields[spots]': 'id,name,category',
            'fields[categories]': 'id',
            'filter[id]': placeIds,
            include: 'residence.agency,spots.category'
          })
          .then(
            (response) => {
              resolve(response)
            },
            (error) =>
              vm.prototype.$ErrorHandler
                .process('', error)
                .then((response) => reject(response))
          )
      })
    }

    Proxy.loadReporterNotifications = () => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.find(
          'reporter',
          Helpers.getSubscriber(vm, store.getters['reporter/token']),
          { 'fields[reporters]': 'notifications' }
        ).then(
          (response) => {
            resolve(response)
          },
          (error) => {
            vm.config.globalProperties.$ErrorHandler
              .process('', error)
              .then((response) => {
                reject(response)
              })
          }
        )
      })
    }

    Proxy.clearReporterNotifications = (notificationInfos) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.headers['Accept'] = 'application/vnd.api+json'
        Proxy.headers['Content-Type'] = 'application/vnd.api+json'
        // FIXME: Change user to reporter on the folowing endpoint (make this available on the API before)
        if (notificationInfos.notifications.length) {
          Proxy.one(
            'user',
            Helpers.getSubscriber(vm, store.getters['reporter/token'])
          )
            .all('notifications')
            .destroy(notificationInfos)
            .then(
              (response) => {
                resolve(response)
              },
              (error) => {
                vm.config.globalProperties.$ErrorHandler
                  .process('', error)
                  .then((response) => {
                    reject(response)
                  })
              }
            )
        }
      })
    }

    Proxy.loadReporterIssues = (
      updatedAt = '1970-01-01T00:00:00.000Z',
      pagelimit = 50
    ) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one(
          'reporter',
          Helpers.getSubscriber(vm, store.getters['reporter/token'])
        )
          .all('issues')
          .get({
            'fields[issues]':
              'disturbance,talks,status,message,people,location,is-private,created-at,updated-at,category,author',
            'fields[categories]': 'id',
            'fields[users]': 'id',
            'page[limit]': pagelimit,
            'filter[since]': updatedAt,
            sort: '-updated-at',
            include: 'category,author'
          })
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => {
                  reject(response)
                })
            }
          )
      })
    }

    // NOTE: the raw error is rejected on purpose so that the caller can display
    // the validation message returned by the API (invalid password format, ...)
    Proxy.updateUserPassword = (password) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.update('user', {
          id: Helpers.getSubscriber(vm, store.getters['reporter/token']),
          password: password
        }).then(
          (response) => {
            resolve(response)
          },
          (error) => {
            console.error('updateUserPassword => Error', error)
            reject(error)
          }
        )
      })
    }

    Proxy.updateIssue = (issue) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.headers['Content-Type'] = 'application/vnd.api+json'
        Proxy.update('issue', {
          id: issue.id,
          status: issue.status,
          code: issue.code
        }).then(
          (response) => {
            resolve(response)
          },
          (error) => {
            vm.config.globalProperties.$ErrorHandler
              .process('', error)
              .then((response) => {
                reject(response)
              })
          }
        )
      })
    }

    Proxy.createIssue = (issue) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.headers['Content-Type'] = 'application/vnd.api+json'
        Proxy.create('issue', { ...issue, metadata: { uid: issue._id } }).then(
          (response) => {
            resolve(response)
          },
          (error) => {
            const title = error[0]?.title || ''
            if (
              title.startsWith('duplicate detected') ||
              title == 'Resource not found'
            ) {
              const message = t('createIssue.unprocessableError')
              vm.config.globalProperties.$ErrorHandler
                .process(message, {}) // NOTE: send an unprocessable error {}
                .then(() => {
                  reject(error)
                })
            } else {
              let message = t('createIssue.error')
              vm.config.globalProperties.$ErrorHandler
                .process(message, error)
                .then((response) => {
                  reject(response)
                })
            }
          }
        )
      })
    }

    Proxy.loadAudit = (auditId) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.find('audit', auditId, {
          'fields[audits]': 'due-at,place,checklist,excluded-checkpoints',
          'fields[places]': 'name,zone,spots',
          'fields[spots]': 'name,category',
          'fields[zones]': 'name',
          'fields[checklists]': 'name,category,checkpoints',
          'fields[categories]': 'id',
          'fields[checkpoints]': 'question,description,category',
          include:
            'place.zone,place.spots.category,checklist.category,checklist.checkpoints.category'
        }).then(
          (response) => {
            resolve(response)
          },
          (error) => {
            const message = `${t('common.auditReloading')}:`
            vm.config.globalProperties.$ErrorHandler
              .process(message, error)
              .then((response) => {
                reject(response)
              })
          }
        )
      })
    }

    Proxy.reloadAudits = async (untillDate) => {
      let result = []
      const PAGINATION = 200
      let recordCount
      const getAudits = (offset, limit) => {
        return new Promise((resolve, reject) => {
          Proxy.headers['Authorization'] =
            'Bearer ' + store.getters['reporter/token']
          Proxy.one(
            'checklister',
            Helpers.getSubscriber(vm, store.getters['reporter/token'])
          )
            .all('audits')
            .get({
              'fields[audits]': 'id',
              'filter[due-until]': untillDate,
              'page[limit]': limit,
              'page[offset]': offset
            })
            .then(
              (response) => {
                resolve(response)
              },
              (error) => {
                const message = `${t('common.auditReloading')}:`
                vm.config.globalProperties.$ErrorHandler
                  .process(message, error)
                  .then((response) => {
                    reject(response)
                  })
              }
            )
        })
      }
      const isRejected = {
        value: false,
        message: undefined
      }
      return new Promise(async (resolve, reject) => {
        do {
          try {
            const response = await getAudits(result.length, PAGINATION)
            result = [...result, ...response.data]
            recordCount = response.meta['record-count']
          } catch (error) {
            isRejected.value = true
            const message = `${t('common.auditReloading')}:`
            vm.config.globalProperties.$ErrorHandler
              .process(message, error)
              .then((response) => {
                isRejected.message = response
              })
          }
        } while (result.length < recordCount)
        return isRejected.value ? reject(isRejected.message) : resolve(result)
      })
    }

    Proxy.loadAuditsByChunks = (auditIds) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one(
          'checklister',
          Helpers.getSubscriber(vm, store.getters['reporter/token'])
        )
          .all('audits')
          .get({
            'filter[id]': auditIds,
            'fields[audits]': 'due-at,place,checklist,excluded-checkpoints',
            'fields[places]': 'name,zone,spots',
            'fields[spots]': 'name,category',
            'fields[zones]': 'name',
            'fields[checklists]': 'name,category,checkpoints',
            'fields[categories]': 'id',
            'fields[checkpoints]': 'question,description,category',
            include:
              'place.zone,place.spots.category,checklist.category,checklist.checkpoints.category'
          })
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              const message = `${t('common.auditReloading')}:`
              vm.config.globalProperties.$ErrorHandler
                .process(message, error)
                .then((response) => {
                  reject(response)
                })
            }
          )
      })
    }

    Proxy.loadPlaceAudits = (placeId) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one('place', placeId)
          .all('audits')
          .get({
            'fields[audits]': 'due-at,place,checklist,excluded-checkpoints',
            'fields[checklists]': 'name,category,checkpoints',
            'fields[places]': 'name,zone,spots',
            'fields[spots]': 'name,category',
            'fields[categories]': 'id,img,base-category',
            'fields[base-categories]': 'id',
            'fields[checkpoints]': 'question,description,category',
            include:
              'place.zone,place.spots.category,checklist.category.base-category,checklist.checkpoints.category',
            'page[limit]': 200
          })
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => {
                  reject(response)
                })
            }
          )
      })
    }

    Proxy.loadCheckpoints = (auditId) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.find('audit', auditId, {
          'fields[audits]': 'checklist,excluded-checkpoints',
          'fields[checklists]': 'id',
          include: 'checklist'
        }).then(
          (auditResponse) => {
            const checklistId = auditResponse.data.checklist.id
            // NOTE: FEAT-4256 — /checklists/:id/checkpoints sert le template
            // complet de la checklist, sans contexte d'audit : il ne peut pas
            // filtrer les exclusions. On remonte les deux listes à l'appelant,
            // qui fait le croisement comme le fait le store.
            const excludedCheckpoints =
              auditResponse.data['excluded-checkpoints']
            Proxy.one('checklist', checklistId)
              .all('checkpoints')
              .get({
                include: 'category',
                'fields[checkpoints]': 'question,description,category',
                'fields[categories]': 'id'
              })
              .then(
                (response) =>
                  resolve({ checkpoints: response.data, excludedCheckpoints }),
                (error) =>
                  vm.config.globalProperties.$ErrorHandler
                    .process('', error)
                    .then((response) => reject(response))
              )
          },
          (error) =>
            vm.config.globalProperties.$ErrorHandler
              .process('', error)
              .then((response) => reject(response))
        )
      })
    }

    Proxy.createReport = (report) => {
      return new Promise((resolve, reject) => {
        report.checkpoints.forEach((chkp) => {
          chkp.asserted == null ? delete chkp.asserted : delete chkp.missing
        })
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.headers['Content-Type'] = 'application/vnd.api+json'
        Proxy.create('report', {
          checkpoints: report.checkpoints,
          audit: { id: report.audit.id },
          spot: report.spotId ? { id: report.spotId } : undefined,
          metadata: { uid: report._id }
        }).then(
          (response) => {
            resolve(response)
          },
          (error) => {
            const title = error[0]?.title || ''
            if (
              title.startsWith('duplicate detected') ||
              title == 'Resource not found'
            ) {
              const message = t('createReport.unprocessableError')
              vm.config.globalProperties.$ErrorHandler
                .process(message, {}) // NOTE: send an unprocessable error {}
                .then(() => {
                  reject(error)
                })
            } else {
              let message = t('issueDetails.unknownError')
              vm.config.globalProperties.$ErrorHandler
                .process(message, error)
                .then((response) => {
                  reject(response)
                })
            }
          }
        )
      })
    }

    Proxy.createTalk = (issueId = '', message = '') => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.headers['Content-Type'] = 'application/vnd.api+json'
        Proxy.create('talk', {
          message: message,
          issue: { id: String(issueId) }
        }).then(
          (response) => {
            resolve(response)
          },
          (error) => {
            let message = t('createTalk.error')
            vm.config.globalProperties.$ErrorHandler
              .process(message, error)
              .then((response) => {
                reject(response)
              })
          }
        )
      })
    }

    Proxy.sendAlert = (lonlat = '', message = '') => {
      return new Promise((resolve, reject) => {
        let endpoint = Proxy.apiUrl + '/alerts'
        let headers = {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          Authorization: 'Bearer ' + store.getters['reporter/token']
        }
        Proxy.axios
          .post(
            endpoint,
            {
              data: {
                type: 'alerts',
                attributes: {
                  lonlat: lonlat,
                  message: message
                }
              }
            },
            { headers }
          )
          .then(
            (response) => resolve(response),
            (error) => {
              let message = t('sendAlert.error')
              vm.config.globalProperties.$ErrorHandler
                .process(message, error)
                .then((response) => {
                  reject(response)
                })
            }
          )
      })
    }

    Proxy.loadCompanyAgencies = (companyId) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one('company', companyId)
          .all('agencies')
          .get({
            'fields[agencies]': 'id,name',
            'page[limit]': 500
          })
          .then(
            (response) => resolve(response),
            (error) => {
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => reject(response))
            }
          )
      })
    }

    Proxy.loadAgencyResidences = (agencyId, hasAudits = false) => {
      return new Promise((resolve, reject) => {
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one('agency', agencyId)
          .all('residences')
          .get({
            'fields[residences]': hasAudits ? 'id,name,has-audits' : 'id,name',
            'page[limit]': 500
          })
          .then(
            (response) => resolve(response),
            (error) =>
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => reject(response))
          )
      })
    }

    Proxy.loadResidencePlaces = (residenceId, hasAudits = false) => {
      return new Promise((resolve, reject) => {
        const { id } = store.getters['company/attributes']
        const companyIds = [191, 21]
        const placesPageLimit = companyIds.includes(id) ? 1000 : 500
        Proxy.headers['Authorization'] =
          'Bearer ' + store.getters['reporter/token']
        Proxy.one('residence', residenceId)
          .all('places')
          .get({
            'fields[places]': `id,name,${hasAudits ? 'has-audits' : 'spots'}`,
            'fields[spots]': 'id,category',
            'fields[categories]': 'id',
            include: 'spots,spots.category',
            // we fetch 1000 places for OPH MONTREUILLOIS FLUX TEL company only
            'page[limit]': placesPageLimit
          })
          .then(
            (response) => resolve(response),
            (error) =>
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => reject(response))
          )
      })
    }

    const _paginatedRequest = (callback) => {
      const PAGINATION = 100
      const isRejected = {
        value: false,
        message: undefined
      }

      return new Promise(async (resolve, reject) => {
        let result = []
        let recordCount
        do {
          try {
            const response = await callback(result.length, PAGINATION)
            result = [...result, ...response.data]
            recordCount = response.meta['record-count']
          } catch (error) {
            isRejected.value = true
            isRejected.message = error
          }
        } while (result.length < recordCount)
        return isRejected.value ? reject(isRejected.message) : resolve(result)
      })
    }

    Proxy.loadPlaceSpots = (placeId) => {
      Proxy.headers['Authorization'] =
        'Bearer ' + store.getters['reporter/token']
      const _getSpots = (offset, limit) => {
        return new Promise((resolve, reject) => {
          Proxy.one('place', placeId)
            .all('spots')
            .get({
              'fields[spots]': 'id,name,category',
              'fields[categories]': 'id',
              'page[limit]': limit,
              'page[offset]': offset,
              include: 'category'
            })
            .then(
              (response) => resolve(response),
              (error) =>
                vm.config.globalProperties.$ErrorHandler
                  .process('', error)
                  .then((response) => reject(response))
            )
        })
      }
      return _paginatedRequest(_getSpots)
    }

    Proxy.loadHousingRelatedSpots = (placeId, categoryId) => {
      Proxy.headers['Authorization'] =
        'Bearer ' + store.getters['reporter/token']

      const _getSpots = (offset, limit) => {
        return new Promise((resolve, reject) => {
          Proxy.one('place', placeId)
            .all('spots')
            .get({
              'fields[spots]': 'name,id',
              'filter[is_housing_related]': true,
              'filter[spots.category]': categoryId,
              'page[offset]': offset,
              'page[limit]': limit
            })
            .then(
              (response) => resolve(response),
              (error) =>
                vm.config.globalProperties.$ErrorHandler
                  .process('', error)
                  .then((response) => reject(response))
            )
        })
      }

      return _paginatedRequest(_getSpots)
    }

    Proxy.forwardIssue = (issue_id, data) => {
      return new Promise((resolve, reject) => {
        Proxy.axios
          .post(Proxy.apiUrl + `/issues/${issue_id}/forward`, data, {
            headers: {
              Accept: 'application/vnd.api+json',
              Authorization: 'Bearer ' + store.getters['reporter/token']
            }
          })
          .then(
            (response) => {
              resolve(response)
            },
            (error) => {
              console.error('sowell-api => forward-issue => error')
              reject(error)
            }
          )
      })
    }

    Proxy.issuesByPlaceId = (placeId, offset = 0, limit = 500) => {
      return new Promise((resolve, reject) => {
        Proxy.one('place', placeId)
          .all('issues')
          .get({
            'fields[categories]': 'id,name',
            'fields[reason]': 'id,name',
            'fields[users]': 'id,first-name,last-name',
            'fields[checkpoint]': 'id,question,checklist',
            'page[offset]': offset,
            'page[limit]': limit,
            sort: '-updated-at',
            include: 'category,reason,checkpoint,checkpoint.checklist,author'
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    }

    Proxy.issuesByResidenceId = (residenceId, offset = 0, limit = 500) => {
      return new Promise((resolve, reject) => {
        Proxy.one('residence', residenceId)
          .all('issues')
          .get({
            'fields[categories]': 'id,name',
            'fields[reason]': 'id,name',
            'fields[users]': 'id,first-name,last-name',
            'fields[checkpoint]': 'id,question,checklist',
            'page[offset]': offset,
            'page[limit]': limit,
            sort: '-updated-at',
            include: 'category,reason,checkpoint,checkpoint.checklist,author'
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    }

    Proxy.sendIssueExport = (data) => {
      return new Promise((resolve, reject) => {
        Proxy.axios
          .post(
            Proxy.apiUrl + '/send-pdf-issue',
            { ...data },
            {
              headers: {
                Accept: 'application/vnd.api+json',
                Authorization: `Bearer ${store.getters['reporter/token']}`
              }
            }
          )
          .then(
            (response) => resolve(response),
            (error) =>
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => reject(response))
          )
      })
    }

    Proxy.sendFailedRequests = (data) => {
      return new Promise((resolve, reject) => {
        Proxy.axios
          .post(
            Proxy.apiUrl + '/send-failed-cron-task',
            { ...data },
            {
              headers: {
                Accept: 'application/vnd.api+json',
                Authorization: `Bearer ${store.getters['reporter/token']}`
              }
            }
          )
          .then(
            (response) => resolve(response),
            (error) =>
              vm.config.globalProperties.$ErrorHandler
                .process('', error)
                .then((response) => reject(response))
          )
      })
    }


    Proxy.loadCompanyProviders = async (companyId) => {
      const PAGINATION = 100
      let result = []

      const getProviders = (offset, limit) => {
        return new Promise((resolve, reject) => {
          Proxy.headers['Authorization'] =
            'Bearer ' + store.getters['reporter/token']
          Proxy.one('company', companyId)
            .all('users')
            .get({
              'fields[users]': 'email,first-name,last-name',
              'filter[roles]': 'provider',
              'page[limit]': limit,
              'page[offset]': offset
            })
            .then(
              (response) => {
                resolve(response)
                console.log('🚀 ~ file: proxy.js:1168 ~ getProviders ~ response:', response)
              },
              (error) => {
                vm.config.globalProperties.$ErrorHandler
                  .process('', error)
                  .then((response) => {
                    reject(response)
                  })
              }
            )
        })
      }

      let recordCount
      do {
        try {
          const response = await getProviders(result.length, PAGINATION)
          result = [...result, ...response.data]
          recordCount = response.meta['record-count']
        } catch (error) {
          if (error) return Promise.reject(error)
        }
      } while (result.length < recordCount)

      return Promise.resolve(result)
    }

    Proxy.loadAllUsers = async (companyId) => {
      const PAGINATION = 100
      let result = []

      const getUsers = (offset, limit) => {
        return new Promise((resolve, reject) => {
          Proxy.headers['Authorization'] =
            'Bearer ' + store.getters['reporter/token']
          Proxy.one('company', companyId)
            .all('users')
            .get({
              'fields[users]': 'email,first-name,last-name,status',
              'page[limit]': limit,
              'page[offset]': offset
            })
            .then(
              (response) => {
                resolve(response)
              },
              (error) => {
                vm.config.globalProperties.$ErrorHandler
                  .process('', error)
                  .then((response) => {
                    reject(response)
                  })
              }
            )
        })
      }

      let recordCount
      do {
        try {
          const response = await getUsers(result.length, PAGINATION)
          result = [...result, ...response.document.data]
          recordCount = response.meta['record-count']
        } catch (error) {
          if (error) return Promise.reject(error)
        }
      } while (result.length < recordCount)

      return Promise.resolve(result)
    }

    Vue.config.globalProperties.$SowellProxy = Proxy
  }
}
